import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const [searchParams] = useSearchParams();
  const sortParam = searchParams.get('sort') || 'latest';

  const currentSlug = categorySlug || 'all';
  const [activeCat, setActiveCat] = useState(currentSlug);
  const [sortBy, setSortBy] = useState(sortParam);
  const [comics, setComics] = useState(() => getComicsByCategory(activeCat));

  useEffect(() => {
    setActiveCat(currentSlug);
    const filtered = getComicsByCategory(currentSlug);
    let sorted = [...filtered];
    if (sortBy === 'popular') {
      sorted.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setComics(sorted);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSlug, sortBy]);

  const activeCategory = categories.find((c) => c.slug === activeCat) || categories[0];

  const breadcrumbItems: BreadcrumbItem[] = [
    { name: '首页', path: '/' },
    { name: '漫画分类', path: '/category' },
    { name: activeCategory.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name}漫画 - 布丁漫画网`,
    description: `布丁漫画网${activeCategory.name}漫画分类，海量${activeCategory.name}类型漫画免费在线阅读，每日更新，流畅阅读体验。`,
    url: `https://budingmanhua.com/category/${activeCat}`,
    isPartOf: { '@type': 'WebSite', name: '布丁漫画网', url: 'https://budingmanhua.com' },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${activeCategory.name}漫画 - 布丁漫画网在线免费阅读`}
        description={`布丁漫画网${activeCategory.name}漫画分类，提供海量${activeCategory.name}类型漫画全集免费在线阅读。无删减内容，下拉式流畅加载，每日更新不重样。`}
        keywords={`${activeCategory.name},漫画,免费阅读,布丁漫画网,在线漫画,下拉式阅读,全集`}
        canonicalUrl={`https://budingmanhua.com/category/${activeCat}`}
        schemaJsonLd={categorySchema}
      />
      <Navbar />
      <main>
        <div className="pt-24 pb-14 md:pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {activeCategory.name === '全部' ? '全部漫画' : `${activeCategory.name}漫画`}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                {activeCategory.name === '全部'
                  ? '浏览全部漫画作品，发现你的下一部必追作品'
                  : `探索${activeCategory.name}类型漫画的精彩世界，共 ${comics.length} 部作品`}
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => {
                    setActiveCat(cat.slug);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                    window.history.pushState({}, '', `/category/${cat.slug === 'all' ? '' : cat.slug}`.replace(/\/$/, '') || '/category');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-foreground-400">排序：</span>
              {[
                { key: 'latest', label: '最新更新' },
                { key: 'popular', label: '最多阅读' },
                { key: 'rating', label: '最高评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => { setSortBy(s.key); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-secondary-100 text-secondary-900 font-medium'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-8">
              {comics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                  {comics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-300" />
                  </div>
                  <p className="mt-4 text-foreground-500">该分类暂无漫画作品</p>
                  <p className="mt-1 text-sm text-foreground-400">请浏览其他分类</p>
                </div>
              )}
            </div>

            {comics.length > 0 && (
              <div className="mt-10 text-center">
                <p className="text-sm text-foreground-400">
                  共找到 <strong className="text-foreground-600">{comics.length}</strong> 部漫画作品
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}